// <!-- AI-authored: Darby's Claude -->
import {
  getEnvironment,
  type ClientEnvironment,
} from "@client/utils/environment";
import { hideLiveHeaderIndicators } from "@client/lib/e2eVisualMode";

/**
 * Header background per environment, so a glance says which deployment you're
 * in. This matters most inside the installed iPad/iPhone app, where there is
 * no URL bar: staging and production TestFlight builds are the same app
 * differing only in display name (Slack decision 2026-08-07, after staff
 * defaulted onto production builds — see mobile/README.md "version lanes").
 *
 * Production stays the plain surface — the tint marks the places it's SAFE to
 * click around, not the dangerous one, so a missing tint (new env, bug here)
 * fails toward "treat it as production".
 *
 * Tokens (GUIDELINES.md §6), not raw palette colors, so both themes work:
 *  - staging + per-PR previews: blue (`info-muted`)
 *  - dev server + locally served builds: amber (`warning-muted`)
 */
export function headerTintClassFor(
  environment: ClientEnvironment,
): string | undefined {
  switch (environment) {
    case "staging":
    case "preview":
      return "bg-info-muted";
    case "development":
    case "local":
      return "bg-warning-muted";
    case "production":
      return undefined;
  }
}

/**
 * The word written on the logo tile in tinted environments, since a color
 * alone is easy to forget the meaning of. Production gets none — same
 * fail-toward-production reasoning as the tint.
 *
 * Three letters, always: the label is painted inside the 32px logo tile, and
 * anything longer clipped at the edges ("staging" showed as "stagin"). Keep
 * new environments to three so the tile never has to grow.
 */
export function environmentLabelFor(
  environment: ClientEnvironment,
): string | undefined {
  switch (environment) {
    case "staging":
      return "stg";
    case "preview":
      return "pre";
    case "development":
    case "local":
      return "dev";
    case "production":
      return undefined;
  }
}

/**
 * The class the live header should apply. E2e runs classify as "development"
 * or "local" (never production), so without the same suppression the live
 * header indicators use — a flag the e2e web server sets on every run — every
 * round-scoped visual snapshot would diff on header color the moment this
 * shipped.
 */
export function getHeaderEnvironmentTintClass(): string | undefined {
  if (hideLiveHeaderIndicators()) {
    return undefined;
  }

  return headerTintClassFor(getEnvironment());
}

/** Live-header counterpart of {@link environmentLabelFor} — same e2e guard. */
export function getEnvironmentLabel(): string | undefined {
  if (hideLiveHeaderIndicators()) {
    return undefined;
  }

  return environmentLabelFor(getEnvironment());
}
